// @flow
import { Fragment } from 'react';
import styled from '@emotion/styled';
import { faUtensils } from '@fortawesome/pro-solid-svg-icons/faUtensils';
import { faWalking } from '@fortawesome/pro-solid-svg-icons/faWalking';
import { animated, useSpring } from 'react-spring/web.cjs';
import { COLORS, SPACING, DEPTH, typography } from '@tablekit/theme';
import Icon from '@tablekit/icon';
import { margin } from '@tablekit/utils';
import { formatDistance } from 'utils/formatDistance';
import { getPageLocaleListSeparator } from 'components/PageContext';
import LoadingSpan from 'components/LoadingSpan';
import { CardSeparator } from './styled';
import type { TCApiShopType } from './types';

type PropsType = {
  apiShop?: TCApiShopType,
  currentLocation: void | { lat: number, lon: number },
  href: string,
  isFindingCurrentLocation: boolean,
  tags: void | string[]
};

const Wrapper = styled.a`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  padding: ${SPACING.L3} 0;
  color: ${COLORS.GRAY_TRANSLUCENT.L6};
`;

const DOT_SIZE = 4;

const FooterElement = styled.div`
  display: flex;
  position: relative;
  align-items: flex-start;
  flex-wrap: wrap;
  ${typography('L1')};
  margin: ${SPACING.L1} ${SPACING.L4};

  & > span {
    margin-top: 1px;
    margin-bottom: 2px;
    position: relative;
  }

  &:not(:last-child) > span:last-child:after {
    display: block;
    content: '';
    position: absolute;
    right: -${SPACING.L4};
    top: 50%;
    transform: translate(50%, -50%);
    width: ${DOT_SIZE}px;
    height: ${DOT_SIZE}px;
    background: currentColor;
    border-radius: 100%;
  }
`;

const DistanceFooterElement = animated(styled(FooterElement)`
  position: relative;
  width: 68px;
  height: 100%;
`);

const DistanceFloatingElement = animated(styled.div`
  position: absolute;
  display: block;
  left: ${SPACING.L4};
  top: ${SPACING.L4};
  padding: ${SPACING.L2};
  background: white;
  ${DEPTH.RAISED};
`);

const FooterIcon = styled(Icon)`
  ${margin({ right: SPACING.L1 })}
`;

const NonBreakSpan = styled.span`
  white-space: nowrap;
  word-break: keep-all;
`;

const Footer = ({
  apiShop,
  href,
  tags,
  isFindingCurrentLocation,
  currentLocation
}: PropsType) => {
  const { geocode } = apiShop || {};
  const localeListSeparator = getPageLocaleListSeparator();

  const footerElements = [];
  if (tags && tags.length) {
    footerElements.push(
      <FooterElement key="cms-tags">
        <FooterIcon icon={faUtensils} />
        {tags.map((tag, index, array) => (
          <NonBreakSpan>
            {tag}
            {index < array.length - 1 ? localeListSeparator : null}
          </NonBreakSpan>
        ))}
      </FooterElement>
    );
  }
  const showDistance =
    !apiShop ||
    (apiShop &&
      apiShop.geocode &&
      (isFindingCurrentLocation || currentLocation));
  const distanceTransition = useSpring({
    opacity: showDistance ? 1 : 0,
    visibility: isFindingCurrentLocation || !apiShop ? 0 : 1
  });

  function renderDistanceWithComponent(Component, key) {
    return (
      <Component
        key={key}
        style={{
          opacity: distanceTransition.opacity,
          display: distanceTransition.opacity.interpolate((v) =>
            v === 0 ? 'none' : 'flex'
          )
        }}
      >
        <FooterIcon icon={faWalking} />
        <LoadingSpan isLoading={isFindingCurrentLocation || !apiShop}>
          {currentLocation && geocode
            ? formatDistance(currentLocation, geocode)
            : '??km'}
        </LoadingSpan>
      </Component>
    );
  }

  if (!tags || !tags.length) {
    if (!showDistance) return null;
    return renderDistanceWithComponent(DistanceFloatingElement);
  }
  if (showDistance) {
    footerElements.push(
      renderDistanceWithComponent(DistanceFooterElement, 'walking-distance')
    );
  }

  return (
    <Fragment>
      <CardSeparator />
      <Wrapper href={href}>{footerElements}</Wrapper>
    </Fragment>
  );
};

export default Footer;
